/**
 * STILL-QUALITY CAPTURE — ask the camera for a photograph, not a video frame.
 *
 * <h2>The defect this exists for (owner, 2026-08-25)</h2>
 *
 * *"From my mobile when trying to capture, always getting 'This camera's photo is too small for a
 * clear copy of your document'. On laptop this is working fine."*
 *
 * <p><strong>Root cause.</strong> The call publishes the camera with no capture constraints at all —
 * `setCameraEnabled(true)`, and whatever the browser negotiates is what we get. On phones that is
 * routinely 640×480, so the short side is 480 against a document floor of 600. The frame is then
 * grabbed off the `<video>` element, which renders that same negotiated stream. A laptop happens to
 * negotiate higher, which is the entire reason it "works fine" there.
 *
 * <p>So the customer with the BETTER camera is the one being refused — and told to
 * *"use a device with a better camera"*. The phone in their hand is a 12-megapixel sensor; we were
 * judging it by the resolution of the compressed video call and blaming the hardware for our own
 * constraint. That is the defect: **a video call and a document photograph are two different jobs
 * with two different resolution needs, and one stream was being asked to do both.**
 *
 * <h2>What this does instead</h2>
 *
 * At the moment of a document shutter, raise the LIVE track to a still-grade resolution, grab, and
 * put it back. Deliberately NOT a second `getUserMedia`: on iOS Safari a second acquisition steals
 * the camera and blacks out the officer's feed — that trap is already recorded in the capture
 * overlay and this must not re-introduce it. `applyConstraints` re-negotiates the EXISTING track,
 * which every current mobile browser supports, including iOS Safari.
 *
 * <p><strong>Restoring matters as much as raising.</strong> A call left running at 1920×1080 on a
 * mobile network is a worse product than one that occasionally re-asks for a photo: the video would
 * stutter for the rest of the session, on exactly the connections least able to afford it. The
 * raise is scoped to the shutter and reverted in a `finally`.
 *
 * <p><strong>Fail-soft, always.</strong> Every step degrades to "capture at whatever we already
 * had": a browser without `applyConstraints`, a camera that cannot reach the target, a constraint
 * that throws — none of them may block a capture the customer is entitled to make. The quality gate
 * still judges the frame afterwards, so a genuinely unusable photo is still caught; what changes is
 * that we ask properly before we refuse.
 */

/** Short side a document capture needs, mirroring `imageQuality` DOC_PHOTO. Kept in sync by test. */
export const DOC_SHORT_SIDE_FLOOR = 600;

/**
 * What we ask the camera for on a document shutter. 1920×1080 is not chosen for its own sake — it is
 * the first widely-supported still size whose SHORT side (1080) clears the document floor with room
 * for the customer to be off-square, which they always are when holding a card up to a phone.
 * `ideal` rather than `exact`: a camera that cannot do it returns its best instead of failing.
 */
const STILL_IDEAL = { width: { ideal: 1920 }, height: { ideal: 1080 } } as const;

/** The measurements a caller needs to decide whether the raise achieved anything. */
export interface TrackSize {
  readonly width: number;
  readonly height: number;
  readonly shortSide: number;
}

export function sizeOf(track: MediaStreamTrack | null | undefined): TrackSize | null {
  if (!track || typeof track.getSettings !== 'function') return null;
  const s = track.getSettings();
  const width = typeof s.width === 'number' ? s.width : 0;
  const height = typeof s.height === 'number' ? s.height : 0;
  if (!width || !height) return null;
  return { width, height, shortSide: Math.min(width, height) };
}

/**
 * Run `grab` with the track raised to still quality, then restore the call's settings.
 *
 * <p>Returns whatever `grab` returns. The raise is best-effort in every direction — if it cannot be
 * applied, `grab` still runs against the unchanged track, because a capture is never worth failing
 * over a resolution request.
 */
export async function withStillQuality<T>(
  track: MediaStreamTrack | null | undefined,
  grab: () => Promise<T>,
  /** Settle time for the sensor to actually deliver the new size (ms). */
  settleMs = 350,
): Promise<T> {
  if (!track || track.readyState !== 'live' || typeof track.applyConstraints !== 'function') {
    return grab();
  }

  // Capture the CALL's settings so they can be restored exactly, rather than guessed at.
  const before = sizeOf(track);
  let raised = false;
  try {
    await track.applyConstraints(STILL_IDEAL);
    raised = true;
    // A constraint resolves when accepted, not when the sensor has re-delivered at the new size.
    // Grabbing immediately can still read the OLD dimensions off the element — which would look
    // exactly like the bug this fixes.
    await new Promise((r) => setTimeout(r, settleMs));
  } catch {
    /* camera declined the raise — capture at what we have */
  }

  try {
    return await grab();
  } finally {
    if (raised && before) {
      // Restore rather than clear: applyConstraints({}) drops to the browser default, which is not
      // necessarily what the call was running at, and a call that silently changes resolution after
      // every photo is its own defect.
      try {
        await track.applyConstraints({
          width: { ideal: before.width },
          height: { ideal: before.height },
        });
      } catch {
        /* best-effort: the call keeps whatever it has rather than dropping the track */
      }
    }
  }
}
